/**
 * Email store — list slice (email-shared path builders + /email/v1 base).
 */
import {
  buildThreadsPath,
  buildDraftPath,
} from '@tshmanager/email-shared'
import {
  emailApiBase,
  normalizeThreadsList,
} from './api.js'

export function attachList(ctx) {
  ctx.buildInboxUrl = function buildInboxUrl(page = 1) {
    const folder = ctx.activeFolder.value
    const mailboxId = ctx.activeMailbox.value

    // buildThreadsPath maps folder_N / custom-N / tag_* via folderKeyToInboxParams.
    const url = new URL(
      buildThreadsPath('', folder || 'inbox', page, {
        mailboxId: mailboxId != null && mailboxId !== '' ? Number(mailboxId) : undefined,
      }),
      window.location.origin
    )

    // Free-text search stays on threads#index (`text`) rather than search#query:
    // only this endpoint keeps mailbox scope, folder, chips and advanced filters.
    const text = ctx.searchText.value.trim()
    if (text) url.searchParams.set('text', text)

    if (mailboxId != null && mailboxId !== '' && !url.searchParams.has('mailbox_id')) {
      url.searchParams.set('mailbox_id', String(mailboxId))
    }

    const qf = ctx.quickFilter.value
    if (qf) url.searchParams.set('quick_filter', qf)

    const sort = ctx.sortOrder.value
    if (sort && sort !== 'newest') url.searchParams.set('sort', sort)

    Object.entries(ctx.advancedFilters.value || {}).forEach(([key, val]) => {
      if (val) url.searchParams.set(key, val)
    })

    // Relativize under /email/v1 (builders used empty base).
    return `${emailApiBase()}${url.pathname}${url.search}`
  }

  ctx.fetchEmails = async function fetchEmails(page = 1) {
    if (ctx.activeFolder.value === 'drafts') {
      return ctx.fetchDrafts()
    }

    const loading = page === 1 ? ctx.emailsLoading : ctx.emailsLoadingMore
    loading.value = true
    try {
      const raw = await ctx.apiGet(ctx.buildInboxUrl(page))
      const data = normalizeThreadsList(raw)
      ctx.batch(() => {
        if (page === 1) {
          ctx.emails.value = data.emails || []
        } else {
          ctx.emails.value = [...ctx.emails.value, ...(data.emails || [])]
        }
        ctx.emailsMeta.value = data.meta || ctx.emailsMeta.value
        loading.value = false
      })
    } catch (err) {
      console.error('[emailStore] fetchEmails error:', err)
      loading.value = false
    }
  }

  ctx.fetchDrafts = async function fetchDrafts() {
    ctx.emailsLoading.value = true
    try {
      let path = buildDraftPath(emailApiBase())
      if (ctx.activeMailbox.value != null) {
        path += `?mailbox_id=${ctx.activeMailbox.value}`
      }
      const drafts = await ctx.apiGet(path)
      const list = drafts?.drafts || (Array.isArray(drafts) ? drafts : [])
      const rows = list.map(d => ctx._formatDraftListRow(d))
      ctx.batch(() => {
        ctx.emails.value = rows
        ctx.emailsMeta.value = { next_page: null, total_count: rows.length, unread_count: 0 }
        ctx.emailsLoading.value = false
      })
    } catch (err) {
      console.error('[emailStore] fetchDrafts error:', err)
      ctx.emailsLoading.value = false
    }
  }

  ctx._draftAddressLabel = function _draftAddressLabel(addr) {
    if (addr == null || addr === '') return ''
    if (typeof addr === 'string') return addr
    if (typeof addr === 'object') return addr.name || addr.address || addr.email || ''
    return String(addr)
  }

  ctx._formatDraftListRow = function _formatDraftListRow(draft) {
    const to0 = Array.isArray(draft.to_addresses) ? draft.to_addresses[0] : null
    return {
      ...draft,
      from_name: ctx._draftAddressLabel(to0) || draft.from_name || '',
      is_draft: true,
      last_activity: draft.last_activity || draft.updated_at,
    }
  }

  ctx.fetchMoreEmails = async function fetchMoreEmails() {
    const next = ctx.emailsMeta.value.next_page
    if (!next || ctx.emailsLoadingMore.value) return
    await ctx.fetchEmails(next)
  }

  ctx.selectThread = async function selectThread(emailId) {
    // Close compose without an intermediate URL sync — one push after thread id is set.
    if (ctx.composeOpen.value) {
      ctx.batch(() => {
        ctx.composeOpen.value = false
        ctx.draftId.value = null
        ctx.composeDirty.value = false
        ctx.composeDraftData.value = null
        ctx.composeOriginalEmailId.value = null
      })
    }

    if (ctx._isDraftListRow(emailId)) {
      await ctx.openDraftById(emailId)
      return
    }

    const id = emailId ? Number(emailId) : null
    ctx.batch(() => {
      ctx.currentThreadId.value = id
      if (ctx.isMobileLayout.value) {
        ctx.showReadingPane()
      } else if (ctx.isCompactNavLayout.value) {
        ctx.showListPanel()
      }
    })
    // Await thread load so deep-link compose=reply can quote from items (#335).
    if (id) await ctx.fetchThread(id)
    ctx.syncInboxUrl()
  }

  ctx.openDraftById = async function openDraftById(draftIdValue) {
    const id = draftIdValue ? Number(draftIdValue) : null
    if (!id) return
    try {
      const draft = await ctx.apiGet(buildDraftPath(emailApiBase(), id))
      const d = draft?.draft || draft
      ctx.batch(() => {
        ctx.currentThreadId.value = null
        ctx.thread.value = null
      })
      ctx.openComposeFromDraft({
        id: d.id,
        mailbox_id: d.mailbox_id,
        subject: d.subject,
        body_html: d.body_html || d.body,
        to_addresses: d.to_addresses,
        cc_addresses: d.cc_addresses,
        draft_type: d.draft_type || 'new',
        reply_to_email_id: d.reply_to_email_id,
      })
      ctx.showReadingPane()
    } catch (err) {
      console.error('[emailStore] openDraftById error:', err)
    }
  }

  // Deep-link HTML route (still /emails/inbox) — not the JSON API.
  ctx.openThreadWindow = function openThreadWindow(emailId) {
    const id = emailId ? Number(emailId) : null
    if (!id) return
    if (ctx._isDraftListRow(id)) {
      ctx.openDraftById(id)
      return
    }

    const row = ctx.emails.value.find(e => Number(e.id) === id)
    const mailboxId = row?.mailbox_id ?? ctx.activeMailbox.value ?? null
    const params = new URLSearchParams({ thread_id: String(id), view: 'thread' })
    if (mailboxId != null && mailboxId !== '') params.set('mailbox_id', String(mailboxId))
    const url = `/emails/inbox?${params.toString()}`
    const win = window.open(url, `email-thread-${id}`, 'width=1280,height=900')
    if (win) win.opener = null
  }

  ctx.threadLoaded = function threadLoaded() {
    ctx.listLoading.value = false
  }

  ctx.toggleSelected = function toggleSelected(id) {
    const numId = Number(id)
    const current = ctx.selectedIds.value
    ctx.selectedIds.value = current.includes(numId)
      ? current.filter(i => i !== numId)
      : [...current, numId]
  }

  ctx.selectAll = function selectAll(ids) {
    ctx.selectedIds.value = ids.map(Number)
  }

  ctx.clearSelection = function clearSelection() {
    ctx.selectedIds.value = []
  }

  ctx.setCurrentThread = function setCurrentThread(emailId) {
    ctx.currentThreadId.value = emailId ? Number(emailId) : null
  }

  ctx.clearCurrentThread = function clearCurrentThread() {
    ctx.batch(() => {
      ctx.currentThreadId.value = null
      ctx.thread.value = null
    })
    ctx.syncInboxUrl()
  }

  ctx.focusNext = function focusNext() {
    const list = ctx.emails.value
    const idx = list.findIndex(e => e.id === ctx.currentThreadId.value)
    if (idx < list.length - 1) ctx.selectThread(list[idx + 1].id)
  }

  ctx.focusPrev = function focusPrev() {
    const list = ctx.emails.value
    const idx = list.findIndex(e => e.id === ctx.currentThreadId.value)
    if (idx > 0) ctx.selectThread(list[idx - 1].id)
  }

  ctx._sortEmailsByActivity = function _sortEmailsByActivity(list) {
    return [...list].sort((a, b) => {
      const ta = new Date(a.last_activity_at || a.last_activity || a.sent_at || a.created_at || 0).getTime()
      const tb = new Date(b.last_activity_at || b.last_activity || b.sent_at || b.created_at || 0).getTime()
      return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0)
    })
  }

  ctx._updateEmailRow = function _updateEmailRow(updatedEmail) {
    if (!updatedEmail) return
    const next = ctx.emails.value.map(e => {
      if (e.id !== updatedEmail.id) return e
      return { ...e, ...updatedEmail }
    })
    ctx.emails.value = ctx._sortEmailsByActivity(next)
  }

  ctx.setSearchText = function setSearchText(text) {
    ctx.searchText.value = text
  }

  ctx.setQuickFilter = function setQuickFilter(filter) {
    ctx.quickFilter.value = filter
  }

  ctx.toggleQuickFilter = function toggleQuickFilter(chip) {
    ctx.quickFilter.value = ctx.quickFilter.value === chip ? '' : chip
  }

  ctx.setSort = function setSort(sort) {
    ctx.sortOrder.value = sort
  }

  ctx.setAdvancedFilter = function setAdvancedFilter(key, value) {
    ctx.advancedFilters.value = { ...ctx.advancedFilters.value, [key]: value }
  }

  ctx.clearAllFilters = function clearAllFilters() {
    ctx.batch(() => {
      ctx.searchText.value = ''
      ctx.quickFilter.value = ''
      ctx.sortOrder.value = 'newest'
      ctx.advancedFilters.value = {}
    })
  }

  ctx.toggleFiltersPanel = function toggleFiltersPanel() {
    ctx.filtersOpen.value = !ctx.filtersOpen.value
  }

  ctx.reloadList = function reloadList() {
    ctx.fetchEmails(1)
  }

  ctx.buildListUrl = function buildListUrl() {
    return ctx.buildInboxUrl()
  }
}
